import { Evented, EventObject } from '@dojo/framework/core/Evented';
import { logSystem } from 'logger';
import BrowserWebSocket from './getWS';

export enum ReadyState {
	CONNECTING,
	OPEN,
	CLOSING,
	CLOSED
}
export enum SendState {
	OKAY,
	NOT_READY,
	BUFFERING,
	SEND_ERROR
}

export interface IWSocket {
	state: ReadyState;
	url: string;
	msgcache: any;
	socket: WebSocket;

	open(force: boolean): void;
	close(): void;
	send(message: any): SendState;
	is_open(): boolean;
}

export interface WSCloseEvent extends EventObject<'close'> {
	event: any;
}

export interface WSOpenEvent extends EventObject<'open'> {
	event: any;
}

export interface WSMessageEvent extends EventObject<'message'> {
	data: any;
}

export interface WSErrorEvent extends EventObject<'error'> {
	event: any;
}

/**
 * WebSocket wrapper to canonicalize differences in WS implementations.
 *
 * @export
 * @class WSocket
 * @implements {IWSocket}
 */
export class WSocket extends Evented implements IWSocket {
	private _state: ReadyState;
	private _socket: WebSocket | null;
	private _msgcache: any;
	private _url: string;

	/**
	 * Creates an instance of WSocket.
	 *
	 * @param {string} url
	 * @param {any} [options={}]
	 *
	 * @memberOf WSocket
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	constructor(url: string, options: any = {}) {
		super();
		this._state = ReadyState.CLOSED;
		this._url = url;
		this._socket = null;
	}

	get state(): ReadyState {
		return this._state;
	}

	get url(): string {
		return this._url;
	}

	get msgcache(): any {
		return this._msgcache;
	}

	get socket(): WebSocket {
		return this._socket as WebSocket;
	}

	handleClose(event: CloseEvent | object) {
		logSystem.info({ msg: 'WS CloseEvent', data: event });
		this._state = ReadyState.CLOSED;
		const socket = this._socket;
		if (socket && socket.readyState < BrowserWebSocket.CLOSING) {
			socket.onclose = null;
			socket.onerror = null;
			socket.onmessage = null;
			socket.close();
		}
		this.emit({ type: 'close', event } as WSCloseEvent);
		this._socket = null;
	}

	/**
	 * Open the WebSocket. If `force` is false, any existing WS will be
	 * left open and nothing will be done.
	 *
	 * @param {boolean} force
	 * @returns {void}
	 *
	 * @memberOf WSocket
	 */
	open(force: boolean): void {
		if (this._socket) {
			if (!force) {
				return;
			}
			const socket = this._socket;
			socket.onclose = null;
			socket.onerror = null;
			socket.onmessage = null;
			if (socket.readyState < BrowserWebSocket.CLOSING) {
				socket.close();
			}
			this._socket = null;
		}

		this._state = ReadyState.CONNECTING;
		const socket = (this._socket = new BrowserWebSocket(this._url));
		socket.onerror = this.handleClose.bind(this);
		logSystem.debug({ msg: 'opening', data: socket });

		socket.onopen = (event: Event): void => {
			this._state = ReadyState.OPEN;
			this.emit({ type: 'open', event } as WSOpenEvent);

			socket.onmessage = (event: MessageEvent): void => {
				this.emit({ type: 'message', data: event.data } as WSMessageEvent);
			};

			socket.onerror = (event: Event): void => {
				this.emit({ type: 'error', event } as WSErrorEvent);
				this.handleClose({});
			};

			socket.onclose = this.handleClose.bind(this);
		};
	}

	/**
	 * Close the WebSocket.
	 *
	 * @memberOf WSocket
	 */
	close(...args: any[]): void {
		this._state = ReadyState.CLOSING;
		if (this._socket) {
			this._socket.close(...args);
		}
	}

	/**
	 * Send a message over the WebSocket.
	 *
	 * @param {*} message
	 * @returns {SendState}
	 *
	 * @memberOf WSocket
	 */
	send(message: any): SendState {
		const socket = this._socket;
		let success = SendState.OKAY;

		if (!this.is_open()) {
			return SendState.NOT_READY;
		}
		if (socket === null) {
			return SendState.NOT_READY;
		}

		// check for out-of-sync state
		if (socket.readyState !== BrowserWebSocket.OPEN) {
			this.handleClose({ reason: '[Internal] WS readyState not OPEN' });
			return SendState.NOT_READY;
		}

		if (socket.bufferedAmount > 0) {
			return SendState.BUFFERING;
		}

		this._msgcache = message;

		try {
			socket.send(message);
			success = SendState.OKAY;
		} catch (e) {
			this.handleClose({ reason: '[Internal] Failed to send message' });
			success = SendState.SEND_ERROR;
		}
		return success;
	}

	/**
	 * Returns true if the WebSocket is open and ready.
	 *
	 * @returns {boolean}
	 *
	 * @memberOf WSocket
	 */
	is_open(): boolean {
		return !!this._socket && this._state === ReadyState.OPEN && this._socket.readyState === BrowserWebSocket.OPEN;
	}
}

export default WSocket;
